
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Building,
  Home,
  MapPin,
  Upload,
  X,
  FileText,
  Plus,
  Image,
  Save,
  Send,
  // QrCode,
  QrCode,
  Loader2
} from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  Autocomplete,
  type Libraries
} from '@react-google-maps/api';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useFieldArray } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MultiSelect } from '@/components/ui/multi-select';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { toast } from '@/hooks/use-toast';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import {
  propertyTypes,
  propertyCategories,
  listingTypes,
  propertyStatuses,
  amenities,
  localities,
  developers,
  pricingTypes,
  currencies,
  bedrooms,
  bathrooms,
  furnishingTypes
} from '@/constants';

import { updateProperty, getPropertyById } from '@/services/property';

const libraries: Libraries = ["places"];
const googleApiKey = import.meta.env.VITE_PUBLIC_GOOGLE_MAPS_API_KEY || "";

// Form schema
const formSchema = z.object({
  // Basic details
  listingType: z.enum(['SALE', 'RENT'], {
    required_error: "Please select a listing type",
  }),
  propertyCategory: z.enum(['RESIDENTIAL', 'COMMERCIAL'], {
    required_error: "Please select a property category",
  }),
  propertyType: z.enum(['Apartment', 'Villa', 'Suite', 'Penthouse', 'Townhouse', 'Bunglow', 'Compound'], {
    required_error: "Please select a property type",
  }),
  title: z.string().min(5, {
    message: "Title must be at least 5 characters",
  }),
  description: z.string().min(50, {
    message: "Description must be at least 50 characters",
  }),
  siteUrl: z.string().url({
    message: "Please enter a valid URL",
  }).optional().or(z.literal('')),

  // Price and status
  pricingType: z.enum(['FIXED', 'NEGOTIABLE'], {
    required_error: "Please select a pricing type",
  }),
  currency: z.enum(['AED'], {
    required_error: "Please select a currency",
  }),
  price: z.string().min(1, "Price is required").refine((val) => !isNaN(Number(val)) && Number(val) > -1, {
    message: "Price must be a positive number",
  }),
  status: z.enum(['AVAILABLE', 'NOT-AVAILABLE', 'OFF-PLAN'], {
    required_error: "Please select a property status",
  }),

  // Payment Plan
  planType: z.string().optional(),
  onBooking: z.string().optional(),
  underConstruction: z.string().optional(),
  onHandover: z.string().optional(),

  // Property details
  titleDeed: z.string({
    required_error: "Title deed is required",
  }).trim(),
  size: z.string().optional(),
  bedrooms: z.array(z.string()).optional(),
  bathrooms: z.string().optional(),
  parkingSpaces: z.string().optional().refine((val) => !val || (!isNaN(Number(val)) && Number(val) >= 0), {
    message: "Parking spaces must be a non-negative number",
  }),
  furnishingType: z.enum(['FURNISHED', 'SEMI-FURNISHED', 'FULL-FURNISHED', 'UNFURNISHED']).optional(),
  builtUpArea: z.string().optional(),
  totalPlotSize: z.string().optional(),
  layoutType: z.string().optional(),
  amenities: z.array(z.string()).optional(),
  location: z.string({
    required_error: "Please select a location",
  }),
  developer: z.string({
    required_error: "Please select a developer",
  }),
  nearbyAttractions: z.array(z.string()).optional(),
  address: z.string().optional(),
  latitude: z.number().optional(),
  longitude: z.number().optional(),

  // Floor Plans
  floorPlans: z.array(z.object({
    beds: z.string().optional(),
    size: z.string().optional(),
    price: z.string().optional(),
    currency: z.string().default('AED'),
  })).optional(),
});

const PropertyEdit = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [images, setImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [floorPlan, setFloorPlan] = useState<File | null>(null);
  const [floorPlanPreview, setFloorPlanPreview] = useState<string | null>(null);
  const [qrCode, setQrCode] = useState<File | null>(null);
  const [qrCodePreview, setQrCodePreview] = useState<string | null>(null);
  const [brochure, setBrochure] = useState<File | null>(null);
  const [threeSixtyImages, setThreeSixtyImages] = useState<File[]>([]);

  const [threeSixtyPreviews, setThreeSixtyPreviews] = useState<string[]>([]);
  const [isImageProcessing, setIsImageProcessing] = useState(false);

  // State for existing media from server
  const [existingImages, setExistingImages] = useState<string[]>([]);
  const [existingThreeSixty, setExistingThreeSixty] = useState<string[]>([]);
  const [existingQrCode, setExistingQrCode] = useState<string>('');
  const [existingBrochure, setExistingBrochure] = useState<string>('');
  const [existingFloorPlan, setExistingFloorPlan] = useState<string>('');

  const [mapCenter, setMapCenter] = useState({
    lat: 25.276987, // Default to Dubai
    lng: 55.296249,
  });
  const [autocomplete, setAutocomplete] = useState<google.maps.places.Autocomplete | null>(null);

  // Initialize form
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      listingType: 'SALE',
      propertyCategory: 'RESIDENTIAL',
      propertyType: 'Apartment',
      title: '',
      description: '',
      siteUrl: '',
      pricingType: 'FIXED',
      currency: 'AED',
      price: '',
      status: 'AVAILABLE',
      planType: '',
      onBooking: '',
      underConstruction: '',
      onHandover: '',
      titleDeed: '',
      size: '',
      bedrooms: [],
      bathrooms: '',
      parkingSpaces: '',
      furnishingType: undefined,
      builtUpArea: '',
      totalPlotSize: '',
      layoutType: '',
      amenities: [],
      location: '',
      developer: '',
      nearbyAttractions: [],
      address: '',
      latitude: 25.276987,
      longitude: 55.296249,
      floorPlans: [],
    },
  });

  const { fields: floorPlanFields, append: appendFloorPlan, remove: removeFloorPlan } = useFieldArray({
    control: form.control,
    name: "floorPlans",
  });

  // Load Google Maps API
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: googleApiKey,
    libraries,
  });

  // Reverse Geocode Function to get Address from Lat/Lng
  const fetchAddressFromCoordinates = useCallback(
    async (lat: number, lng: number) => {
      const geocodeUrl = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${googleApiKey}`;

      try {
        const response = await fetch(geocodeUrl);
        const data = await response.json();

        if (data.status === "OK" && data.results.length > 0) {
          const formattedAddress = data.results[0].formatted_address;
          form.setValue('address', formattedAddress);
          form.setValue('latitude', lat);
          form.setValue('longitude', lng);
        }
      } catch (error) {
        console.error("Error fetching address:", error);
      }
    },
    [form]
  );

  // Get User's Current Location on Component Load
  useEffect(() => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setMapCenter({ lat: latitude, lng: longitude });
        // Optional: Don't auto-set address on load to avoid overwriting if editing (though this is create only)
        // fetchAddressFromCoordinates(latitude, longitude);
      },
      (error) => console.error("Geolocation Error:", error),
      { enableHighAccuracy: true }
    );
  }, []);

  // Fetch property data for editing
  useEffect(() => {
    const fetchProperty = async () => {
      if (!id) {
        navigate('/broker/properties');
        return;
      }

      try {
        setIsFetching(true);
        const response = await getPropertyById(id);
        const property = response.property;

        // Populate form fields
        form.reset({
          listingType: property.listingType || 'SALE',
          propertyCategory: property.propertyCategory || 'RESIDENTIAL',
          propertyType: property.specifications?.propertyType || 'Apartment',
          title: property.specifications?.titleDeed || '',
          description: property.overview || '',
          siteUrl: property.siteURL || '',
          pricingType: property.pricing?.type || 'FIXED',
          currency: property.pricing?.currency || 'AED',
          price: property.pricing?.amount?.toString() || '',
          status: property.management?.availability || 'AVAILABLE',
          planType: property.paymentPlan?.planType || '',
          onBooking: property.paymentPlan?.onBooking || '',
          underConstruction: property.paymentPlan?.underConstruction || '',
          onHandover: property.paymentPlan?.onHandover || '',
          titleDeed: property.specifications?.titleDeed || '',
          size: property.specifications?.size || '',
          bedrooms: property.specifications?.beds
            ? property.specifications.beds.split(/,\s*|\s*&\s*/).map((s: string) => s.trim()).filter(Boolean)
            : [],
          bathrooms: property.specifications?.bathrooms?.toString() || '',
          parkingSpaces: property.specifications?.parkingSpaces?.toString() || '',
          furnishingType: property.specifications?.furnishingType,
          builtUpArea: property.specifications?.builtUpArea || '',
          totalPlotSize: property.specifications?.totalPlotSize || '',
          layoutType: property.specifications?.layoutType || '',
          amenities: property.amenities ? Object.keys(property.amenities).filter(key => property.amenities[key]) : [],
          location: property.location?.locality || '',
          developer: property.management?.developer || '',
          nearbyAttractions: property.nearbyLocations || [],
          address: property.location?.address || '',
          latitude: property.location?.coordinates?.latitude || 25.276987,
          longitude: property.location?.coordinates?.longitude || 55.296249,
          floorPlans: property.floorplan?.map((fp: any) => ({
            beds: fp.beds,
            size: fp.size,
            price: fp.pricing?.amount?.toString() || '',
            currency: fp.pricing?.currency || 'AED'
          })) || [],
        });

        // Set map center
        if (property.location?.coordinates) {
          setMapCenter({
            lat: property.location.coordinates.latitude,
            lng: property.location.coordinates.longitude,
          });
        }

        // Set existing media
        setExistingImages(property.media?.images || []);
        setExistingThreeSixty(property.media?.threeSixtyMedia || []);
        setExistingQrCode(property.media?.qrCode || '');
        setExistingBrochure(property.supportFile?.brochure || '');
        setExistingFloorPlan(property.supportFile?.floorPlan || '');

      } catch (error) {
        console.error('Error fetching property:', error);
        toast({
          title: "Error",
          description: "Failed to load property data.",
          variant: "destructive",
        });
        navigate('/broker/properties');
      } finally {
        setIsFetching(false);
      }
    };

    fetchProperty();
  }, [id, navigate, form]);

  // Handle Autocomplete Address Selection
  const handlePlaceSelect = () => {
    if (autocomplete) {
      const place = autocomplete.getPlace();
      if (place.geometry && place.geometry.location) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        setMapCenter({ lat, lng });
        fetchAddressFromCoordinates(lat, lng);
      }
    }
  };

  // Handle Map Click for Selecting a Location
  const handleMapClick = (event: google.maps.MapMouseEvent) => {
    if (event.latLng) {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      setMapCenter({ lat, lng });
      fetchAddressFromCoordinates(lat, lng);
    }
  };


  // Handle image uploads
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setIsImageProcessing(true);
      const fileList = Array.from(e.target.files);
      setImages(prev => [...prev, ...fileList]);

      try {
        // Create previews
        const newPreviews = await Promise.all(fileList.map(file => {
          return new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              resolve(reader.result as string);
            };
            reader.readAsDataURL(file);
          });
        }));

        setImagePreviews(prev => [...prev, ...newPreviews]);
      } catch (error) {
        console.error("Error processing images:", error);
      } finally {
        setIsImageProcessing(false);
      }
    }
  };

  // Remove image from preview
  const removeImage = (index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  // Handle 360 media uploads
  const handleThreeSixtyUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const fileList = Array.from(e.target.files);
      setThreeSixtyImages(prev => [...prev, ...fileList]);

      // Create previews
      fileList.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setThreeSixtyPreviews(prev => [...prev, reader.result as string]);
        };
        reader.readAsDataURL(file);
      });
    }
  };

  // Remove 360 media from preview
  const removeThreeSixtyImage = (index: number) => {
    setThreeSixtyImages(prev => prev.filter((_, i) => i !== index));
    setThreeSixtyPreviews(prev => prev.filter((_, i) => i !== index));
  };

  // Handle floor plan upload
  const handleFloorPlanUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setFloorPlan(file);

      const reader = new FileReader();
      reader.onloadend = () => {
        setFloorPlanPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle brochure upload
  const handleBrochureUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setBrochure(file);
    }
  };

  // Handle QR code upload
  const handleQrCodeUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setQrCode(file);

      const reader = new FileReader();
      reader.onloadend = () => {
        setQrCodePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Remove existing image
  const removeExistingImage = (index: number) => {
    setExistingImages(prev => prev.filter((_, i) => i !== index));
  };

  // Remove existing 360 image
  const removeExistingThreeSixty = (index: number) => {
    setExistingThreeSixty(prev => prev.filter((_, i) => i !== index));
  };

  // Unified submission handler
  const handleSubmission = async (values: z.infer<typeof formSchema>, status: 'PENDING' | 'DRAFT') => {
    setIsLoading(true);

    try {
      const formData = new FormData();

      // Basic Fields
      formData.append('listingType', values.listingType);
      formData.append('propertyCategory', values.propertyCategory);
      formData.append('status', status);
      formData.append('overview', values.description);
      formData.append('siteURL', values.siteUrl || '');

      // Specifications
      const specifications = {
        titleDeed: values.title, // Title mapped to titleDeed
        propertyType: values.propertyType,
        size: values.size,
        beds: values.bedrooms && values.bedrooms.length > 0
          ? values.bedrooms.length > 1
            ? `${values.bedrooms.slice(0, -1).join(', ')} & ${values.bedrooms[values.bedrooms.length - 1]}`
            : values.bedrooms[0]
          : '',
        bathrooms: values.bathrooms,
        parkingSpaces: values.parkingSpaces,
        furnishingType: values.furnishingType,
        builtUpArea: values.builtUpArea,
        totalPlotSize: values.totalPlotSize,
        layoutType: values.layoutType,
      };
      formData.append('specifications', JSON.stringify(specifications));

      // Pricing
      const pricing = {
        type: values.pricingType,
        amount: values.price ? Number(values.price) : 0,
        currency: values.currency,
      };
      formData.append('pricing', JSON.stringify(pricing));

      // Location
      const location = {
        locality: values.location,
        address: values.address,
        coordinates: {
          latitude: values.latitude,
          longitude: values.longitude,
        },
      };
      formData.append('location', JSON.stringify(location));

      // Amenities (Convert array to Map/Object)
      const amenitiesMap = (values.amenities || []).reduce((acc: any, curr: string) => {
        acc[curr] = true;
        return acc;
      }, {});
      formData.append('amenities', JSON.stringify(amenitiesMap));

      // Nearby Locations
      const nearbyLocations = values.nearbyAttractions
        ?.map((item) => item.replace(/^\d+\.\s*/, '').trim())
        .filter((item) => item.length > 0) || [];
      formData.append('nearbyLocations', JSON.stringify(nearbyLocations));

      // Payment Plan
      const paymentPlan = {
        planType: values.planType,
        onBooking: values.onBooking,
        underConstruction: values.underConstruction,
        onHandover: values.onHandover,
      };
      formData.append('paymentPlan', JSON.stringify(paymentPlan));

      const management = {
        developer: values.developer,
        availability: values.status, // Property availability status
      };
      formData.append('management', JSON.stringify(management));

      // Floor Plans Details
      if (values.floorPlans && values.floorPlans.length > 0) {
        const floorPlanData = values.floorPlans.map(fp => ({
          beds: fp.beds,
          size: fp.size,
          pricing: {
            amount: fp.price ? fp.price : 0,
            currency: fp.currency || 'AED'
          }
        }));
        formData.append('floorplan', JSON.stringify(floorPlanData));
      }

      // Floor Plans Details
      if (values.floorPlans && values.floorPlans.length > 0) {
        const floorPlanData = values.floorPlans.map(fp => ({
          beds: fp.beds,
          size: fp.size,
          pricing: {
            amount: fp.price ? Number(fp.price) : 0,
            currency: fp.currency || 'AED'
          }
        }));
        formData.append('floorplan', JSON.stringify(floorPlanData));
      }

      // Files - append existing media URLs
      formData.append('existingImages', JSON.stringify(existingImages));
      formData.append('existingThreeSixtyMedia', JSON.stringify(existingThreeSixty));
      formData.append('existingQrCode', existingQrCode);
      formData.append('existingBrochure', existingBrochure);
      formData.append('existingFloorPlan', existingFloorPlan);

      // New files
      images.forEach((file) => {
        formData.append('images', file);
      });

      threeSixtyImages.forEach((file) => {
        formData.append('threeSixtyMedia', file);
      });

      if (qrCode) {
        formData.append('qrCode', qrCode);
      }

      if (brochure) {
        formData.append('brochure', brochure);
      }

      if (floorPlan) {
        formData.append('floorPlan', floorPlan);
      }

      // Call API - use updateProperty instead of createProperty
      if (!id) {
        throw new Error('Property ID is missing');
      }
      await updateProperty(id, formData);

      toast({
        title: status === 'DRAFT' ? "Draft Saved" : "Property Updated",
        description: status === 'DRAFT'
          ? "Your property listing has been saved as a draft."
          : "Your property listing has been updated successfully.",
      });

      // Navigate back to properties page
      navigate('/broker/properties');
    } catch (error: any) {
      console.error('Submission error:', error);
      toast({
        title: "Submission Failed",
        description: error.message || "An error occurred while submitting the property.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Save as draft
  const saveAsDraft = () => {
    const values = form.getValues();
    // Check if at least title and property category are filled
    if (values.title && values.propertyCategory) {
      handleSubmission(values, 'DRAFT');
    } else {
      toast({
        title: "Cannot Save Draft",
        description: "Please fill at least the property title and category.",
        variant: "destructive",
      });
    }
  };

  // Submit form
  const onSubmit = (values: z.infer<typeof formSchema>) => {
    handleSubmission(values, 'PENDING');
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      {isFetching ? (
        <div className="pt-32 pb-20 bg-secondary/30 flex-1">
          <div className="container mx-auto px-4 md:px-6">
            <div className="mb-8">
              <Skeleton className="h-4 w-48 mb-2" />
              <Skeleton className="h-10 w-64 mb-1" />
              <Skeleton className="h-4 w-48 mt-1" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-8">
                {[1, 2, 3].map((i) => (
                  <Card key={i}>
                    <CardHeader>
                      <Skeleton className="h-6 w-32 mb-2" />
                      <Skeleton className="h-4 w-64" />
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <Skeleton className="h-10 w-full" />
                      <Skeleton className="h-20 w-full" />
                      <div className="grid grid-cols-2 gap-4">
                        <Skeleton className="h-10 w-full" />
                        <Skeleton className="h-10 w-full" />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              <div className="space-y-8">
                <Card>
                  <CardHeader>
                    <Skeleton className="h-6 w-24 mb-2" />
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <Skeleton className="h-24 w-full" />
                    <Skeleton className="h-10 w-full" />
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader>
                    <Skeleton className="h-6 w-24 mb-2" />
                  </CardHeader>
                  <CardContent>
                    <Skeleton className="h-40 w-full" />
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="pt-32 pb-20 bg-secondary/30 flex-1">
          <div className="container mx-auto px-4 md:px-6">
            <div className="mb-8">
              <div className="flex text-sm text-muted-foreground mb-2">
                <Link to="/broker/dashboard" className="hover:text-foreground">Dashboard</Link>
                <span className="mx-2">/</span>
                <Link to="/broker/properties" className="hover:text-foreground">Properties</Link>
                <span className="mx-2">/</span>
                <span className="font-medium text-foreground">Edit Property</span>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold">Edit Property</h1>
              <p className="text-muted-foreground mt-1">Update the property details</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                    {/* Basic Property Details */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-xl">Basic Details</CardTitle>
                        <CardDescription>
                          Enter the fundamental information about the property
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        {/* Listing Type */}
                        <FormField
                          control={form.control}
                          name="listingType"
                          render={({ field }) => (
                            <FormItem className="space-y-3">
                              <FormLabel>Listing Type</FormLabel>
                              <FormControl>
                                <RadioGroup
                                  onValueChange={field.onChange}
                                  defaultValue={field.value}
                                  className="flex flex-col space-y-1"
                                >
                                  {listingTypes.map((type) => (
                                    <div key={type.value} className="flex items-center space-x-2">
                                      <RadioGroupItem value={type.value} id={type.value} />
                                      <Label htmlFor={type.value}>{type.label}</Label>
                                    </div>
                                  ))}
                                </RadioGroup>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {/* Property Category */}
                        <FormField
                          control={form.control}
                          name="propertyCategory"
                          render={({ field }) => (
                            <FormItem className="space-y-3">
                              <FormLabel>Property Category</FormLabel>
                              <FormControl>
                                <RadioGroup
                                  onValueChange={field.onChange}
                                  defaultValue={field.value}
                                  className="flex flex-col space-y-1"
                                >
                                  {propertyCategories.map((category) => (
                                    <div key={category.value} className="flex items-center space-x-2">
                                      <RadioGroupItem value={category.value} id={category.value} />
                                      <Label htmlFor={category.value}>{category.label}</Label>
                                    </div>
                                  ))}
                                </RadioGroup>
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {/* Title */}
                        <FormField
                          control={form.control}
                          name="title"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Property Title</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="Enter a catchy title for the property"
                                  {...field}
                                />
                              </FormControl>
                              <FormDescription>
                                This will be the main title for your property listing.
                              </FormDescription>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {/* Description */}
                        <FormField
                          control={form.control}
                          name="description"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Property Description</FormLabel>
                              <FormControl>
                                <Textarea
                                  placeholder="Provide a detailed description of the property"
                                  className="min-h-[150px]"
                                  {...field}
                                />
                              </FormControl>
                              <FormDescription>
                                Include key features, condition, and unique selling points.
                              </FormDescription>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {/* Site URL */}
                        <FormField
                          control={form.control}
                          name="siteUrl"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Site URL (Optional)</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="https://example.com/property"
                                  {...field}
                                />
                              </FormControl>
                              <FormDescription>
                                External website or virtual tour link for the property.
                              </FormDescription>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </CardContent>
                    </Card>

                    {/* Price & Status */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-xl">Price & Status</CardTitle>
                        <CardDescription>
                          Set the pricing and availability status
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        {/* Pricing Type */}
                        <FormField
                          control={form.control}
                          name="pricingType"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Pricing Type</FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                value={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select pricing type" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {pricingTypes.map((type) => (
                                    <SelectItem key={type.value} value={type.value}>
                                      {type.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {/* Currency */}
                        <FormField
                          control={form.control}
                          name="currency"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Currency</FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                value={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select currency" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {currencies.map((currency) => (
                                    <SelectItem key={currency.value} value={currency.value}>
                                      {currency.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {/* Price */}
                        <FormField
                          control={form.control}
                          name="price"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Price</FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  placeholder="e.g. 1500000"
                                  {...field}
                                />
                              </FormControl>
                              <FormDescription>
                                Enter the price without commas or currency symbol.
                              </FormDescription>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {/* Status */}
                        <FormField
                          control={form.control}
                          name="status"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Property Status</FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                defaultValue={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select property status" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {propertyStatuses.map((status) => (
                                    <SelectItem key={status.value} value={status.value}>
                                      {status.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormDescription>
                                Current availability status of the property.
                              </FormDescription>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </CardContent>
                    </Card>

                    {/* Payment Plan */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-xl">Payment Plan</CardTitle>
                        <CardDescription>
                          Set the payment plan details for the property
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        {/* Plan Type */}
                        <FormField
                          control={form.control}
                          name="planType"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Plan Type</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="Enter plan type"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {/* On Booking */}
                        <FormField
                          control={form.control}
                          name="onBooking"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>On Booking</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="e.g. 10%"
                                  {...field}
                                />
                              </FormControl>
                              <FormDescription>
                                Payment percentage or amount required on booking.
                              </FormDescription>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {/* Under Construction */}
                        <FormField
                          control={form.control}
                          name="underConstruction"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Under Construction</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="e.g. 40%"
                                  {...field}
                                />
                              </FormControl>
                              <FormDescription>
                                Payment percentage or amount during construction phase.
                              </FormDescription>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {/* On Handover */}
                        <FormField
                          control={form.control}
                          name="onHandover"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>On Handover</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="e.g. 50%"
                                  {...field}
                                />
                              </FormControl>
                              <FormDescription>
                                Payment percentage or amount required on handover.
                              </FormDescription>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </CardContent>
                    </Card>

                    {/* Property Details */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-xl">Property Details</CardTitle>
                        <CardDescription>
                          Add specific information about the property
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-6">

                        {/* Property Type */}
                        <FormField
                          control={form.control}
                          name="propertyType"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Property Type</FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                value={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select property type" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {propertyTypes.map((type) => (
                                    <SelectItem key={type.value} value={type.label}>
                                      {type.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {/* Size */}
                        <FormField
                          control={form.control}
                          name="size"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Size (sqft)</FormLabel>
                              <FormControl>
                                <Input
                                  type="text"
                                  placeholder="e.g. 1500"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {/* Bedrooms */}
                        <FormField
                          control={form.control}
                          name="bedrooms"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Number of Bedrooms</FormLabel>
                              <FormControl>
                                <MultiSelect
                                  options={bedrooms}
                                  selected={field.value || []}
                                  onChange={field.onChange}
                                  placeholder="Select number of bedrooms"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {/* Bathrooms */}
                        <FormField
                          control={form.control}
                          name="bathrooms"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Number of Bathrooms</FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                defaultValue={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select number of bathrooms" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {bathrooms.map((bathroom) => (
                                    <SelectItem key={bathroom.value} value={bathroom.value}>
                                      {bathroom.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {/* Parking Spaces */}
                        <FormField
                          control={form.control}
                          name="parkingSpaces"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Parking Spaces</FormLabel>
                              <FormControl>
                                <Input
                                  type="number"
                                  placeholder="e.g. 2"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {/* Furnishing Type */}
                        <FormField
                          control={form.control}
                          name="furnishingType"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Furnishing Type</FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                value={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select furnishing type" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {furnishingTypes.map((type) => (
                                    <SelectItem key={type.value} value={type.value}>
                                      {type.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {/* Built Up Area */}
                        <FormField
                          control={form.control}
                          name="builtUpArea"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Built Up Area (sqft)</FormLabel>
                              <FormControl>
                                <Input
                                  type="text"
                                  placeholder="e.g. 2000"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {/* Total Plot Size */}
                        <FormField
                          control={form.control}
                          name="totalPlotSize"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Total Plot Size (sqft)</FormLabel>
                              <FormControl>
                                <Input
                                  type="text"
                                  placeholder="e.g. 3000"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {/* Layout Type */}
                        <FormField
                          control={form.control}
                          name="layoutType"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Layout Type</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="Enter layout type"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {/* Amenities */}
                        <FormField
                          control={form.control}
                          name="amenities"
                          render={() => (
                            <FormItem>
                              <div className="mb-4">
                                <FormLabel>Amenities</FormLabel>
                                <FormDescription>
                                  Select all amenities available in the property.
                                </FormDescription>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {amenities.map((amenity) => (
                                  <FormField
                                    key={amenity.value}
                                    control={form.control}
                                    name="amenities"
                                    render={({ field }) => {
                                      return (
                                        <FormItem
                                          key={amenity.value}
                                          className="flex flex-row items-start space-x-3 space-y-0"
                                        >
                                          <FormControl>
                                            <Checkbox
                                              checked={field.value?.includes(amenity.label)}
                                              onCheckedChange={(checked) => {
                                                return checked
                                                  ? field.onChange([...(field.value || []), amenity.label])
                                                  : field.onChange(
                                                    field.value?.filter(
                                                      (value) => value !== amenity.label
                                                    )
                                                  )
                                              }}
                                            />
                                          </FormControl>
                                          <FormLabel className="font-normal">
                                            {amenity.label}
                                          </FormLabel>
                                        </FormItem>
                                      )
                                    }}
                                  />
                                ))}
                              </div>
                              <FormMessage />
                            </FormItem>
                          )}
                        />



                        {/* Developer */}
                        <FormField
                          control={form.control}
                          name="developer"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Developer</FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                defaultValue={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select developer" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {developers.map((developer) => (
                                    <SelectItem key={developer.value} value={developer.value}>
                                      {developer.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                      </CardContent>
                    </Card>

                    {/* Location Details */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-xl">Location Details</CardTitle>
                        <CardDescription>
                          Specify the property location and nearby attractions
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        {/* Address Autocomplete */}
                        {isLoaded ? (
                          <div className="space-y-4">
                            <FormField
                              control={form.control}
                              name="address"
                              render={({ field }) => (
                                <FormItem>
                                  <FormLabel>Property Address</FormLabel>
                                  <FormControl>
                                    <Autocomplete
                                      onLoad={(auto) => setAutocomplete(auto)}
                                      onPlaceChanged={handlePlaceSelect}
                                    >
                                      <Input
                                        placeholder="Search for property address"
                                        {...field}
                                      />
                                    </Autocomplete>
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />

                            {/* Map */}
                            <div className="rounded-md border overflow-hidden h-[400px]">
                              <GoogleMap
                                mapContainerStyle={{ width: "100%", height: "100%" }}
                                center={mapCenter}
                                zoom={14}
                                onClick={handleMapClick}
                              >
                                <Marker position={mapCenter} />
                              </GoogleMap>
                            </div>
                            <p className="text-sm text-muted-foreground">
                              Click on the map to select a location or search above.
                            </p>
                          </div>
                        ) : (
                          <div className="h-[400px] bg-muted animate-pulse rounded-md flex items-center justify-center">
                            <p className="text-muted-foreground">Loading Maps...</p>
                          </div>
                        )}

                        {/* Location */}
                        <FormField
                          control={form.control}
                          name="location"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Locality</FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                defaultValue={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger>
                                    <SelectValue placeholder="Select locality" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {localities.map((locality) => (
                                    <SelectItem key={locality.value} value={locality.value}>
                                      {locality.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        {/* Nearby Attractions */}
                        <FormField
                          control={form.control}
                          name="nearbyAttractions"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Nearby Attractions</FormLabel>
                              <FormControl>
                                <Textarea
                                  placeholder={`1. Dubai Mall (5 min drive)\n2. Downtown Hotel (10 min drive)\n3. City Hospital (15 min drive)`}
                                  className="min-h-[100px]"
                                  {...field}
                                  value={field.value?.join('\n') || ''}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    field.onChange(val.split('\n'));
                                  }}
                                />
                              </FormControl>
                              <FormDescription>
                                List nearby attractions one per line. Numbering is optional.
                              </FormDescription>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </CardContent>
                    </Card>

                    {/* Upload Sections */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-xl">Upload Media</CardTitle>
                        <CardDescription>
                          Add images and documents for the property
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-6">
                        {/* Property Images */}
                        <div className="space-y-3">
                          <Label className="text-base">Property Images</Label>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-2">
                            {/* Existing Images */}
                            {existingImages.map((src, index) => (
                              <div key={`existing-${index}`} className="relative group">
                                <div className="aspect-square rounded-md overflow-hidden border bg-muted">
                                  <img
                                    src={src}
                                    alt={`Existing ${index}`}
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                                <button
                                  type="button"
                                  onClick={() => removeExistingImage(index)}
                                  className="absolute top-1 right-1 bg-black/50 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            ))}

                            {/* Image Previews */}
                            {imagePreviews.map((src, index) => (
                              <div key={index} className="relative group">
                                <div className="aspect-square rounded-md overflow-hidden border bg-muted">
                                  <img
                                    src={src}
                                    alt={`Preview ${index}`}
                                    className="w-full h-full object-cover"
                                  />
                                </div>
                                <button
                                  type="button"
                                  onClick={() => removeImage(index)}
                                  className="absolute top-1 right-1 bg-black/50 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            ))}

                            {/* Loading Indicator */}
                            {isImageProcessing && (
                              <div className="aspect-square rounded-md border bg-muted flex items-center justify-center">
                                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                              </div>
                            )}

                            {/* Upload Button */}
                            <div className="aspect-square rounded-md border-2 border-dashed flex items-center justify-center bg-muted hover:bg-muted/70 transition-colors cursor-pointer">
                              <label htmlFor="property-images" className="cursor-pointer w-full h-full flex flex-col items-center justify-center">
                                <div className="flex flex-col items-center justify-center p-4">
                                  <Image className="h-8 w-8 text-muted-foreground mb-2" />
                                  <span className="text-sm font-medium text-muted-foreground">Add Images</span>
                                </div>
                                <input
                                  id="property-images"
                                  type="file"
                                  multiple
                                  accept="image/*"
                                  className="hidden"
                                  onChange={handleImageUpload}
                                />
                              </label>
                            </div>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            Upload high-quality images of the property (max 10 images).
                          </p>
                        </div>

                        <Separator />

                        {/* Floor Plan */}
                        <div className="space-y-3">
                          <Label className="text-base">Upload Floor Plan File</Label>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                            {/* Existing Floor Plan */}
                            {existingFloorPlan && (
                              <div className="relative group">
                                <div className="aspect-video rounded-md overflow-hidden border bg-muted flex items-center justify-center">
                                  {existingFloorPlan.toLowerCase().endsWith('.pdf') ? (
                                    <a href={existingFloorPlan} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center p-4 hover:opacity-80 transition-opacity">
                                      <FileText className="h-12 w-12 text-muted-foreground mb-2" />
                                      <span className="text-xs text-muted-foreground">View PDF</span>
                                    </a>
                                  ) : (
                                    <img
                                      src={existingFloorPlan}
                                      alt="Existing Floor Plan"
                                      className="w-full h-full object-contain"
                                    />
                                  )}
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setExistingFloorPlan('')}
                                  className="absolute top-1 right-1 bg-black/50 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            )}

                            {/* Floor Plan Preview */}
                            {floorPlanPreview && (
                              <div className="relative group">
                                <div className="aspect-video rounded-md overflow-hidden border bg-muted">
                                  <img
                                    src={floorPlanPreview}
                                    alt="Floor Plan Preview"
                                    className="w-full h-full object-contain"
                                  />
                                </div>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setFloorPlan(null);
                                    setFloorPlanPreview(null);
                                  }}
                                  className="absolute top-1 right-1 bg-black/50 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            )}

                            {/* Upload Button */}
                            {!floorPlanPreview && !existingFloorPlan && (
                              <div className="aspect-video rounded-md border-2 border-dashed flex items-center justify-center bg-muted hover:bg-muted/70 transition-colors cursor-pointer">
                                <label htmlFor="floor-plan" className="cursor-pointer w-full h-full flex flex-col items-center justify-center">
                                  <div className="flex flex-col items-center justify-center p-4">
                                    <FileText className="h-8 w-8 text-muted-foreground mb-2" />
                                    <span className="text-sm font-medium text-muted-foreground">Upload Floor Plan</span>
                                  </div>
                                  <input
                                    id="floor-plan"
                                    type="file"
                                    accept="image/*,.pdf"
                                    className="hidden"
                                    onChange={handleFloorPlanUpload}
                                  />
                                </label>
                              </div>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            Upload a PDF or image file of the property floor plan.
                          </p>
                        </div>

                        <Separator />

                        {/* Floor Plan Details */}
                        <div className="space-y-4">
                          <Label className="text-base">Floor Plans</Label>
                          <div className="space-y-4">
                            {floorPlanFields.map((field, index) => (
                              <div key={field.id} className="relative grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 p-4 border rounded-md">
                                <Button
                                  type="button"
                                  variant="destructive"
                                  size="icon"
                                  className="absolute -top-3 -right-3 h-6 w-6 rounded-full shadow-sm z-10"
                                  onClick={() => removeFloorPlan(index)}
                                >
                                  <X className="h-3 w-3" />
                                </Button>

                                <FormField
                                  control={form.control}
                                  name={`floorPlans.${index}.beds`}
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel className="">Beds</FormLabel>
                                      <FormControl>
                                        <Input {...field} placeholder="e.g. 2 Bedroom Apartment" className="h-9" />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />

                                <FormField
                                  control={form.control}
                                  name={`floorPlans.${index}.price`}
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel className="">Price Range</FormLabel>
                                      <FormControl>
                                        <Input {...field} placeholder="e.g. 1.5M" className="h-9" />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />

                                <FormField
                                  control={form.control}
                                  name={`floorPlans.${index}.size`}
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel className="">Size</FormLabel>
                                      <FormControl>
                                        <Input {...field} placeholder="e.g. 1500 sqft" className="h-9" />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                              </div>
                            ))}

                            <Button
                              type="button"
                              variant="outline"
                              onClick={() => appendFloorPlan({ beds: '', size: '', price: '', currency: 'AED' })}
                              className="w-full"
                            >
                              <Plus className="h-4 w-4 mr-2" />
                              Add Floor Plan
                            </Button>
                          </div>
                        </div>

                        <Separator />

                        {/* QR Code */}
                        <div className="space-y-3">
                          <Label className="text-base">Property QR Code</Label>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-2">
                            {/* Existing QR Code */}
                            {existingQrCode && (
                              <div className="relative group">
                                <div className="aspect-square rounded-md overflow-hidden border bg-muted flex items-center justify-center">
                                  {existingQrCode.toLowerCase().endsWith('.pdf') ? (
                                    <a href={existingQrCode} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center p-4 hover:opacity-80 transition-opacity">
                                      <FileText className="h-8 w-8 text-muted-foreground mb-2" />
                                      <span className="text-xs text-muted-foreground">View PDF</span>
                                    </a>
                                  ) : (
                                    <img
                                      src={existingQrCode}
                                      alt="Existing QR Code"
                                      className="w-full h-full object-contain"
                                    />
                                  )}
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setExistingQrCode('')}
                                  className="absolute top-1 right-1 bg-black/50 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            )}

                            {/* QR Code Preview */}
                            {qrCodePreview && (
                              <div className="relative group">
                                <div className="aspect-square rounded-md overflow-hidden border bg-muted">
                                  <img
                                    src={qrCodePreview}
                                    alt="QR Code Preview"
                                    className="w-full h-full object-contain"
                                  />
                                </div>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setQrCode(null);
                                    setQrCodePreview(null);
                                  }}
                                  className="absolute top-1 right-1 bg-black/50 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            )}

                            {/* Upload Button */}
                            {!qrCodePreview && !existingQrCode && (
                              <div className="aspect-square rounded-md border-2 border-dashed flex items-center justify-center bg-muted hover:bg-muted/70 transition-colors cursor-pointer">
                                <label htmlFor="qr-code" className="cursor-pointer w-full h-full flex flex-col items-center justify-center">
                                  <div className="flex flex-col items-center justify-center p-4">
                                    <QrCode className="h-8 w-8 text-muted-foreground mb-2" />
                                    <span className="text-sm font-medium text-muted-foreground">Upload QR</span>
                                  </div>
                                  <input
                                    id="qr-code"
                                    type="file"
                                    accept="image/*,.pdf"
                                    className="hidden"
                                    onChange={handleQrCodeUpload}
                                  />
                                </label>
                              </div>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            Upload a QR code for the property (optional).
                          </p>
                        </div>

                        <Separator />

                        {/* 360 Media */}
                        {/* ... (360 media section commented out by user) ... */}

                        {/* Brochure */}
                        <div className="space-y-3">
                          <Label className="text-base">Brochure</Label>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                            {/* Existing Brochure */}
                            {existingBrochure && (
                              <div className="relative group">
                                <div className="flex items-center p-4 border rounded-md bg-muted">
                                  <FileText className="h-8 w-8 text-muted-foreground mr-3" />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium truncate">{decodeURIComponent(existingBrochure.split('/').pop() || 'Brochure')}</p>
                                    <a href={existingBrochure} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline">View File</a>
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setExistingBrochure('')}
                                  className="absolute top-1 right-1 bg-black/50 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            )}

                            {/* Brochure Preview */}
                            {brochure && (
                              <div className="relative group">
                                <div className="flex items-center p-4 border rounded-md bg-muted">
                                  <FileText className="h-8 w-8 text-muted-foreground mr-3" />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium truncate">{brochure.name}</p>
                                    <p className="text-xs text-muted-foreground">{(brochure.size / 1024 / 1024).toFixed(2)} MB</p>
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setBrochure(null)}
                                  className="absolute top-1 right-1 bg-black/50 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            )}

                            {/* Upload Button */}
                            {!brochure && !existingBrochure && (
                              <div className="h-20 rounded-md border-2 border-dashed flex items-center justify-center bg-muted hover:bg-muted/70 transition-colors cursor-pointer">
                                <label htmlFor="brochure" className="cursor-pointer w-full h-full flex flex-col items-center justify-center">
                                  <div className="flex flex-row items-center justify-center p-4">
                                    <FileText className="h-6 w-6 text-muted-foreground mr-2" />
                                    <span className="text-sm font-medium text-muted-foreground">Upload Brochure</span>
                                  </div>
                                  <input
                                    id="brochure"
                                    type="file"
                                    accept=".pdf,.doc,.docx"
                                    className="hidden"
                                    onChange={handleBrochureUpload}
                                  />
                                </label>
                              </div>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            Upload a brochure for the property (PDF, DOC).
                          </p>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Form Actions */}
                    <div className="flex justify-between">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={saveAsDraft}
                      >
                        <Save className="h-4 w-4 mr-2" />
                        Save as Draft
                      </Button>
                      <div className="space-x-2">
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => navigate('/broker/dashboard')}
                        >
                          Cancel
                        </Button>
                        <Button type="submit" disabled={isLoading}>
                          {isLoading ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Submitting...
                            </>
                          ) : (
                            <>
                              <Send className="h-4 w-4 mr-2" />
                              Submit for Approval
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                  </form>
                </Form>
              </div>

              {/* Sidebar */}
              <div className="lg:col-span-1">
                <div className="sticky top-24">
                  <Card className="mb-6">
                    <CardHeader>
                      <CardTitle className="text-xl">Upload Guidelines</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex items-start space-x-3">
                        <div className="bg-primary/10 p-2 rounded-full">
                          <Image className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <h4 className="font-medium">High-Quality Images</h4>
                          <p className="text-sm text-muted-foreground">Upload clear, well-lit photos of the property.</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <div className="bg-primary/10 p-2 rounded-full">
                          <FileText className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <h4 className="font-medium">Detailed Descriptions</h4>
                          <p className="text-sm text-muted-foreground">Provide comprehensive information about features and amenities.</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <div className="bg-primary/10 p-2 rounded-full">
                          <MapPin className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <h4 className="font-medium">Accurate Location</h4>
                          <p className="text-sm text-muted-foreground">Select the precise area and highlight nearby attractions.</p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-3">
                        <div className="bg-primary/10 p-2 rounded-full">
                          <Building className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <h4 className="font-medium">Complete Details</h4>
                          <p className="text-sm text-muted-foreground">Include all property specifications for better visibility.</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-xl">Submission Process</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="flex items-center">
                          <div className="bg-gold text-white rounded-full w-6 h-6 flex items-center justify-center mr-3">
                            1
                          </div>
                          <p className="text-sm">Fill out the property details form</p>
                        </div>
                        <div className="flex items-center">
                          <div className="bg-gold text-white rounded-full w-6 h-6 flex items-center justify-center mr-3">
                            2
                          </div>
                          <p className="text-sm">Upload images and documents</p>
                        </div>
                        <div className="flex items-center">
                          <div className="bg-gold text-white rounded-full w-6 h-6 flex items-center justify-center mr-3">
                            3
                          </div>
                          <p className="text-sm">Save as draft or submit for approval</p>
                        </div>
                        <div className="flex items-center">
                          <div className="bg-gold text-white rounded-full w-6 h-6 flex items-center justify-center mr-3">
                            4
                          </div>
                          <p className="text-sm">Wait for admin review and approval</p>
                        </div>
                        <div className="flex items-center">
                          <div className="bg-gold text-white rounded-full w-6 h-6 flex items-center justify-center mr-3">
                            5
                          </div>
                          <p className="text-sm">Property goes live on the website</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default PropertyEdit;
